import React, { useState, useEffect } from 'react';
import ProviderIcons from './ProviderIcons';
import { useLocation } from 'react-router-dom';
import './HomePage.css';
import SearchBar from './SearchBar';
import SearchResults from './SearchResults';
import MediaGrid from './MediaGrid';
import Cookies from 'js-cookie';
import PromptStrip from './PromptStrip';
import './HomePage.css';
// Maps TV genre IDs to readable names
const TV_GENRES = {
  10759: "Action & Adventure", 16: "Animation", 35: "Comedy", 80: "Crime", 99: "Documentary",
  18: "Drama", 10751: "Family", 10762: "Kids", 9648: "Mystery", 10763: "News",
  10764: "Reality", 10765: "Sci-Fi & Fantasy", 10766: "Soap", 10767: "Talk", 10768: "War & Politics", 37: "Western"
};

// Maps Movie genre IDs to readable names
const MOVIE_GENRES = {
  28: "Action", 12: "Adventure", 16: "Animation", 35: "Comedy", 80: "Crime", 99: "Documentary",
  18: "Drama", 10751: "Family", 14: "Fantasy", 36: "History", 27: "Horror", 10402: "Music",
  9648: "Mystery", 10749: "Romance", 878: "Science Fiction", 10770: "TV Movie",
  53: "Thriller", 10752: "War", 37: "Western"
};

const HomePage = () => {
  const [visibleMedia, setVisibleMedia] = useState({ movies: [], tv: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [castData, setCastData] = useState({});
  const [videoData, setVideoData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  // New state for watchlist search matches
  const [watchlistMatches, setWatchlistMatches] = useState([]);
  // Track which item is being deleted for fade-out animation
  const [deletingItemId, setDeletingItemId] = useState(null);
  // Handles search and filters watchlist items for matches
  const [sortMode, setSortMode] = useState('alphabetical');

  const cycleSortMode = () => {
    setSortMode(prev =>
      prev === 'alphabetical' ? 'rating' :
        prev === 'rating' ? 'recent' :
          'alphabetical'
    );
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    const trimmedTerm = term.trim();
    if (trimmedTerm.length >= 3) {
      const storedMovies = JSON.parse(localStorage.getItem('sortedWatchList-movies')) || [];
      const storedTV = JSON.parse(localStorage.getItem('sortedWatchList-tv-shows')) || [];
      const allItems = [...storedMovies, ...storedTV];
      const matched = allItems.filter(item =>
        item.title?.toLowerCase().includes(trimmedTerm.toLowerCase())
      );
      setWatchlistMatches(matched);
    } else {
      setWatchlistMatches([]);
      setSearchTerm('');
    }
  };
  // Read section param from URL to set initial media type
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sectionParam = queryParams.get('section');
  const [activeMediaType, setActiveMediaType] = useState(
    sectionParam === 'movies' ? 'Movies' : 'TV'
  );
  // State for tracking expanded trailer
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  // Show/hide welcome panel, persisted in localStorage
  const [showWelcome, setShowWelcome] = useState(() => {
    const stored = localStorage.getItem('showWelcome');
    return stored === null ? true : stored === 'true';
  });
  // Persist the welcome panel state in localStorage
  useEffect(() => {
    localStorage.setItem('showWelcome', showWelcome);
  }, [showWelcome]);
  const username = Cookies.get("username") || 'default';
  document.title = 'Watch It Buddy';

  // Loads trailer videos for watchlist items
  const fetchVideos = async () => {
    const newVideoData = {};
    for (const type of ['tv', 'movies']) {
      for (const item of visibleMedia[type]) {
        try {
          const res = await fetch(`/api/videos/${type === 'tv' ? 'tv' : 'movie'}/${item.tmdb_id}`);
          const data = await res.json();
          newVideoData[item.tmdb_id] = data.videos?.results || [];
        } catch (e) {
          console.error(`Error fetching videos for ${item.title}`, e);
        }
      }
    }
    setVideoData(newVideoData);
  };

  // Trigger video fetch when media becomes available
  useEffect(() => {
    if (visibleMedia['tv'].length || visibleMedia['movies'].length) {
      fetchVideos();
    }
  }, [visibleMedia]);

  const scrollContainer = (direction, containerId) => {
    const container = document.getElementById(containerId);
    if (container) {
      const scrollAmount = 600;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    const storedMoviesList = localStorage.getItem('sortedWatchList-movies');
    const storedTVList = localStorage.getItem('sortedWatchList-tv-shows');

    const parsedMoviesList = storedMoviesList ? JSON.parse(storedMoviesList) : [];
    const parsedTVList = storedTVList ? JSON.parse(storedTVList) : [];

    setVisibleMedia({ movies: parsedMoviesList, tv: parsedTVList });

    setIsLoading(false);
  }, [username]);

  // Loads cast details for TV and movie items
  useEffect(() => {
    const fetchCasts = async () => {
      const newCastData = {};
      for (const type of ['tv', 'movies']) {
        for (const item of visibleMedia[type]) {
          try {
            const res = await fetch(`/api/cast/${type === 'tv' ? 'tv' : 'movie'}/${item.tmdb_id}`);
            const data = await res.json();
            newCastData[item.tmdb_id] = data.cast || [];
          } catch (e) {
            console.error(`Error fetching cast for ${item.title}`, e);
          }
        }
      }
      setCastData(newCastData);
    };
    if (visibleMedia['tv'].length || visibleMedia['movies'].length) {
      fetchCasts();
    }
  }, [visibleMedia]);

  // Listen for watchlistUpdated event to refresh visibleMedia in real time
  useEffect(() => {
    const handleWatchlistUpdated = (e) => {
      console.log('>>>> Watchlist updated for media type:', e.detail.mediaType);
      const updatedMediaType = e.detail.mediaType;
      const storageKey = `sortedWatchList-${updatedMediaType}`;
      const updatedList = JSON.parse(localStorage.getItem(storageKey) || '[]');
      setVisibleMedia(prev => ({ ...prev, [updatedMediaType]: updatedList }));
    };

    window.addEventListener("watchlistUpdated", handleWatchlistUpdated);
    return () => window.removeEventListener("watchlistUpdated", handleWatchlistUpdated);
  }, []);


  // --- Providers state and fetching logic (moved outside renderQuickPicks) ---
  const [providers, setProviders] = useState({});
  useEffect(() => {
    const fetchProviders = async () => {
      const allProviders = {};
      for (const type of ['tv', 'movies']) {
        for (const item of visibleMedia[type]) {
          try {
            const res = await fetch(`/w/providers?tmdb_id=${item.tmdb_id}&media_type=${type === 'tv' ? 'tv' : 'movie'}`);
            const data = await res.json();
            allProviders[item.tmdb_id] = data.providers || [];
          } catch (e) {
            console.error(`Error fetching providers for ${item.title}`, e);
          }
        }
      }
      setProviders(allProviders);
    };
    if (visibleMedia['tv'].length || visibleMedia['movies'].length) {
      fetchProviders();
    }
  }, [visibleMedia]);

  // Renders detailed cards for TV or Movie watchlist items
  const renderQuickPicks = (type) => {
    
    const finalList = watchlistMatches.length > 0
      ? visibleMedia[type].filter(item =>
        watchlistMatches.some(match => match.tmdb_id === item.tmdb_id)
      )
      : visibleMedia[type];
    const sortedList = [...finalList];
    if (sortMode === 'rating') {
      sortedList.sort((a, b) => (b.vote_average || 0) - (a.vote_average || 0));
    } else if (sortMode === 'recent') {
      sortedList.sort((a, b) =>
        new Date(b.release_date || b.first_air_date || '') - new Date(a.release_date || a.first_air_date || '')
      );
    } else {
      sortedList.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }      
    const quickPickUrl = `/quick-pick?m=${type}`;
    const quickPickLabel = type === 'tv' ? '🎯 Quick Pick TV' : '🎬 Quick Pick Movies';
    return (
      <section className="homepage-section">
        <div style={{ textAlign: 'center', marginBottom: '0px' }}>
          <a href={quickPickUrl} className="quick-pick-button" style={{ fontSize: '18px', padding: '0px 20px' }}>
            {quickPickLabel}
          </a>
        </div>
        <div>
          {sortedList.map((item, index) => {
            const tmdbLink = item.tmdb_id
              ? `https://www.themoviedb.org/${type === 'tv' ? 'tv' : 'movie'}/${item.tmdb_id}/watch`
              : `https://www.themoviedb.org/search?query=${encodeURIComponent(item.title || '')}`;
            const genreNames = item.genre_ids
              ? String(item.genre_ids)
                .split(',')
                .map(id => parseInt(id.trim(), 10))
                .map(id => (type === 'tv' ? TV_GENRES[id] : MOVIE_GENRES[id]))
                .filter(Boolean)
                .join(', ')
              : (item.genre || item.genres || '');
            // The rest of the map logic remains unchanged
            return (
              <div
                className={`list-item${deletingItemId === item.tmdb_id ? ' fade-out' : ''}`}
                key={index}
                style={{
                  backgroundImage: `url(https://image.tmdb.org/t/p/w1066_and_h600_bestv2/${item.backdrop_path || item.poster_path})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '40px',
                  padding: '20px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  backgroundBlendMode: 'overlay',
                  position: 'relative', // <-- ensure relative for absolute children
                }}
              >
                <button
                  onClick={() => {
                    setDeletingItemId(item.tmdb_id);
                    setTimeout(() => {
                      const updatedList = visibleMedia[type].filter((i) => i.tmdb_id !== item.tmdb_id);
                      setVisibleMedia((prev) => ({ ...prev, [type]: updatedList }));

                      const storageKey = `sortedWatchList-${type}`;
                      const currentStored = JSON.parse(localStorage.getItem(storageKey) || '[]');
                      const restoredList = currentStored.filter((stored) => stored.tmdb_id !== item.tmdb_id);
                      localStorage.setItem(storageKey, JSON.stringify(restoredList));
                      setDeletingItemId(null);
                    }, 400); // delay for fade-out animation
                  }}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '30px',
                    height: '30px',
                    fontSize: '18px',
                    cursor: 'pointer',
                    zIndex: 3
                  }}
                  disabled={deletingItemId === item.tmdb_id}
                >
                  ×
                </button>
                {providers[item.tmdb_id]?.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '50px',
                    zIndex: 2,
                    display: 'flex',
                    gap: '6px',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    maxWidth: '50%',
                  }}>
                    <ProviderIcons providers={providers[item.tmdb_id]} mediaType={type === 'tv' ? 'tv' : 'movie'} />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <a href={tmdbLink} target="_blank" rel="noreferrer">
                    <img
                      src={item.full_poster_path}
                      alt={item.title}
                      className="poster-image"
                    />
                  </a>
                  {videoData[item.tmdb_id]?.length > 0 && (
                    <button
                      onClick={() => setExpandedVideoId(prev => prev === item.tmdb_id ? null : item.tmdb_id)}
                      className="trailer-button"
                    >
                      {expandedVideoId === item.tmdb_id ? 'Hide Trailer' : 'Show Trailer'}
                    </button>
                  )}
                </div>
                <div className="media-text-content">
                  <a
                    href={tmdbLink}
                    target="_blank"
                    rel="noreferrer"
                    className="media-title-link"
                  >
                    {item.title}
                  </a>
                  {type === 'tv' ? (
                    <div className="media-subtext">
                      {(item.release_date || item.first_air_date || '').slice(0, 4)} | {genreNames}
                    </div>
                  ) : (
                    <div className="media-subtext">
                      {(item.release_date || item.first_air_date || '').slice(0, 4)} | {genreNames}
                    </div>
                  )}
                  <p className="media-overview">
                    {(item.overview || item.description || 'No description available.')}{' '}
                    {type === 'tv' && (
                      <a
                        href={`https://www.themoviedb.org/tv/${item.tmdb_id}/seasons`}
                        target="_blank"
                        rel="noreferrer"
                        className="episodes-link"
                        style={{ color: '#61dafb', textDecoration: 'none', fontSize: '0.95rem', marginLeft: '6px' }}
                      >
                        📺 View All Episodes
                      </a>
                    )}
                  </p>
                  {castData[item.tmdb_id]?.length > 0 && (
                    <div className="cast-grid">
                      {castData[item.tmdb_id].slice(0, 5).map((actor, i) => (
                        <div key={i} className="cast-item">
                          <a href={`https://www.themoviedb.org/person/${actor.actor_id}`} target="_blank" rel="noreferrer">
                            <img
                              src={actor.profile_path ? `https://image.tmdb.org/t/p/w200${actor.profile_path}` : ''}
                              alt={actor.actor_name}
                              onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                              style={{ width: '100%', maxWidth: '90px', borderRadius: '6px' }}
                            />
                            <div className="cast-placeholder">
                              <i className="fa fa-user" style={{ fontSize: '40px', color: '#777' }}></i>
                            </div>
                          </a>
                          <div className="cast-name">{actor.actor_name}</div>
                          <div className="cast-role">{actor.character}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Trailer iframe inserted below the content */}
                {expandedVideoId === item.tmdb_id && videoData[item.tmdb_id]?.length > 0 && (
                  <div style={{ marginTop: '20px', width: '100%' }}>
                    <iframe
                      width="100%"
                      height="315"
                      src={`https://www.youtube.com/embed/${videoData[item.tmdb_id][0].key}`}
                      title="Trailer"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // Sends the selected item to the server to be stored in the watchlist
  const addToWatchList = async (item) => {
    try {
      await fetch('/api/watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      });
      alert(`${item.title} added to your watch list.`);
    } catch (err) {
      console.error('Failed to add to watchlist:', err);
    }
  };


  // Structure of the homepage: optional welcome panel, media toggles, and watchlist renderings
  return (
    <main className="homepage-main">
      <style>{`
        @media (max-width: 768px) {
          .list-item {
            flex-direction: column !important;
          }
          .list-item img {
            width: 100% !important;
            max-width: 100% !important;
            height: auto;
            border-radius: 6px;
          }
          .cast-grid img {
            max-width: 70px !important;
          }
          .homepage-section {
            flex-direction: column !important;
            padding: 30px 10px !important;
          }
        }
        @media (max-width: 480px) {
          .quick-pick-container .quick-pick-img {
            display: none;
          }
          .quick-pick-container .quick-pick-buttons {
            display: flex;
            gap: 10px;
            justify-content: center;
            margin-top: 20px;
            flex-wrap: wrap;
          }
          .quick-pick-container .quick-pick-button {
            display: inline-block !important;
          }
        }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @media (hover: hover) {
          .media-details-wrapper a:hover {
            text-decoration-color: #61dafb;
            color: #61dafb;
          }
        }
        @media (min-width: 769px) {
          .backdrop-poster {
            display: block !important;
          }
        }
        .fade-out {
          opacity: 0;
          transition: opacity 0.4s ease;
        }
      `}</style>
      {/* Responsive layout for poster/text beside each other when no video */}
      <style>{`
        .media-no-video {
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }
        .poster-wrapper {
          flex-shrink: 0;
          width: 200px;
        }
        .media-details-wrapper {
          flex: 1;
          color: white;
        }
        @media (max-width: 768px) {
          .media-no-video {
            flex-direction: column;
            align-items: center;
          }
          .poster-wrapper {
            width: 100%;
          }
        }
        .sort-toggle-container {
          display: flex;
          justify-content: center;
          margin-bottom: 0px;
          gap: 10px;
          flex-wrap: wrap;
        }

        .desktop-sort-options {
          display: flex;
          gap: 10px;
        }

        .mobile-sort-option {
          display: none;
        }

        @media (max-width: 768px) {
          .desktop-sort-options {
            display: none;
          }
          .mobile-sort-option {
            display: block;
          }
        }          
      `}</style>
      {/* Media type toggle buttons and section buttons moved above welcome panel */}
      <div className="media-toggle-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0' }}>
        <div className="media-toggle">
          <button
            onClick={() => setActiveMediaType('TV')}
            className={`media-toggle-button ${activeMediaType === 'TV' ? 'active' : ''}`}
          >
            TV
          </button>
          <button
            onClick={() => setActiveMediaType('Movies')}
            className={`media-toggle-button ${activeMediaType === 'Movies' ? 'active' : ''}`}
          >
            Movies
          </button>
        </div>
        <div className="discover-group" style={{ display: 'flex', gap: '10px' }}>
          <button
            className="discover-btn active"
            disabled
            style={{
              backgroundColor: '#444',
              color: '#ccc',
              cursor: 'default'
            }}
          >
            Top Picks
          </button>

          {/* Watchlist button inserted between Quick Pick and Discover */}
          <a
            href={`/quick-pick-elimination?m=${activeMediaType === 'TV' ? 'tv-shows' : 'movies'}`}
            className="discover-btn"
          >
            Watch list
          </a>
        </div>
      </div>
      {/* Quick Pick TV/Movies images/buttons directly under toggles and welcome panel */}
      <section className="homepage-section" style={{ paddingTop: '0px' }}>
        <SearchBar
          showQuote={true}
          initialSearchTerm={searchTerm}
          onSearch={handleSearch}
          watchlistMatches={watchlistMatches}
        />
        <SearchResults
          results={[]}
          watchlistMatches={watchlistMatches}
          onRecommendationsFetched={() => { }}
          fetchRecommendations={() => { }}
        />
      </section>
      <div className="sort-toggle-container">
        <div className="desktop-sort-options">
          <button className={`btn btn-sm ${sortMode === 'alphabetical' ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => setSortMode('alphabetical')}>
             A → Z
          </button>
          <button className={`btn btn-sm ${sortMode === 'rating' ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => setSortMode('rating')}>
            <i className="fas fa-star" style={{ marginRight: '6px' }}></i> Rating
          </button>
          <button className={`btn btn-sm ${sortMode === 'recent' ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => setSortMode('recent')}>
            <i className="fas fa-clock" style={{ marginRight: '6px' }}></i> Recently Added
          </button>
        </div>
        <div className="mobile-sort-option">
          <button className="btn btn-sm btn-outline-light" onClick={cycleSortMode}>
            <i className={`fas ${
              sortMode === 'alphabetical' ? 'fa-sort-alpha-down' :
              sortMode === 'rating' ? 'fa-star' :
              'fa-clock'
            }`} style={{ marginRight: '6px' }}></i>
            Sort: {sortMode === 'alphabetical' ? 'A → Z' : sortMode === 'rating' ? 'Rating' : 'Recently Added'}
          </button>
        </div>
      </div>
      {/* Media type toggle bar moved above PromptStrip */}
      {/* Empty state message below toggle buttons */}
      {activeMediaType === 'TV' && visibleMedia['tv'].length === 0 && (
        <div style={{ textAlign: 'center', color: '#bbb', marginTop: '40px', fontSize: '1.1rem' }}>
          Your TV watchlist is empty.<br />
          Try <a href="/quick-pick?m=tv" style={{ color: '#0d6efd', textDecoration: 'none' }}>Quick Pick</a> or use the <a href="/search" style={{ color: '#0d6efd', textDecoration: 'none' }}>Search</a> bar to find something new.
        </div>
      )}
      {activeMediaType === 'Movies' && visibleMedia['movies'].length === 0 && (
        <div style={{ textAlign: 'center', color: '#bbb', marginTop: '40px', fontSize: '1.1rem' }}>
          Your Movies watchlist is empty.<br />
          Try <a href="/quick-pick?m=movies" style={{ color: '#0d6efd', textDecoration: 'none' }}>Quick Pick</a> or use the <a href="/search" style={{ color: '#0d6efd', textDecoration: 'none' }}>Search</a> bar to find something new.
        </div>
      )}
      {/* Section rendering based on activeMediaType */}
      {activeMediaType === 'TV' && (
        <>
          {renderQuickPicks('tv')}
        </>
      )}
      {activeMediaType === 'Movies' && (
        <>
          {renderQuickPicks('movies')}
        </>
      )}
    </main>
  );
};
// Removed unused handleQuickPickSelection and other removed code.
export default HomePage;